import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input, Label } from '../components/ui/Input';
import { apiFetch } from '../lib/apiFetch';
import { useT } from '../i18n';

export function Settings() {
  const t = useT();
  const [tgId, setTgId] = useState('');
  const [role, setRole] = useState<'moderator' | 'admin' | 'super_admin'>('admin');
  const [target, setTarget] = useState('');
  const qc = useQueryClient();

  const promote = useMutation({
    mutationFn: ({ telegram_id, role }: { telegram_id: number; role: string }) =>
      apiFetch('/api/admin/admins/promote', { method: 'POST', body: { telegram_id, role } }),
    onSuccess: () => {
      setTgId('');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const demote = useMutation({
    mutationFn: ({ telegram_id }: { telegram_id: number }) =>
      apiFetch('/api/admin/admins/demote', { method: 'POST', body: { telegram_id } }),
    onSuccess: () => {
      setTarget('');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.promoteTitle')}</CardTitle>
          <CardDescription>{t('settings.promoteDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>{t('settings.telegramId')}</Label>
              <Input value={tgId} onChange={(e) => setTgId(e.target.value)} placeholder="123456789" className="mt-1" />
            </div>
            <div>
              <Label>{t('common.role')}</Label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as typeof role)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
              >
                <option value="moderator">{t('settings.roleModerator')}</option>
                <option value="admin">{t('settings.roleAdmin')}</option>
                <option value="super_admin">{t('settings.roleSuperAdmin')}</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => promote.mutate({ telegram_id: Number(tgId), role })}
                disabled={!tgId.trim() || !Number.isFinite(Number(tgId))}
                loading={promote.isPending}
              >
                <UserPlus className="h-4 w-4" />
                {t('settings.promote')}
              </Button>
            </div>
          </div>
          {promote.isError && (
            <p className="text-sm text-red-600">{(promote.error as { message?: string }).message}</p>
          )}
          {promote.isSuccess && <p className="text-sm text-emerald-600">{t('settings.done')}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.demoteTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Input
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={t('settings.telegramId')}
            />
            <Button
              variant="destructive"
              onClick={() => demote.mutate({ telegram_id: Number(target) })}
              disabled={!target.trim() || !Number.isFinite(Number(target))}
              loading={demote.isPending}
            >
              {t('settings.demote')}
            </Button>
          </div>
          {demote.isError && <p className="text-sm text-red-600">{(demote.error as { message?: string }).message}</p>}
          {demote.isSuccess && <p className="text-sm text-emerald-600">{t('settings.done')}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
